import React from 'react';
import { Bar, BarChart, CartesianGrid, XAxis, YAxis, Cell } from 'recharts';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '../../ui/card';
import {
  ChartConfig,
  ChartContainer,
  ChartLegend,
  ChartLegendContent,
  ChartTooltip,
  ChartTooltipContent,
} from '../../ui/chart';
import { OrdersByDayProps } from './home.types';

//import { ORDER_STATUSES } from '../../../constants';
import { useHistory } from 'react-router-dom/cjs/react-router-dom.min';

const chartConfig = {
  completed: {
    label: 'Completed',
    color: 'hsl(var(--chart-1))',
  },
  outfordelivery: {
    label: 'Out for delivery',
    color: 'hsl(var(--chart-6))',
  },
  awaitingnextattempt: {
    label: 'Awaiting next attempt',
    color: 'hsl(var(--chart-7))',
  },
  intransit: {
    label: 'In Transit',
    color: 'hsl(var(--chart-2))',
  },

  expected: {
    label: 'Expected',
    color: 'hsl(var(--chart-3))',
  },
  draft: {
    label: 'Draft',
    color: 'hsl(var(--chart-4))',
  },
  failed: {
    label: 'Failed',
    color: 'hsl(var(--chart-5))',
  },
} satisfies ChartConfig;

export function OrdersByDay({ ordersByDay, range }: OrdersByDayProps & { range: string }) {
  const history = useHistory();

  // Transform API Data to Chart-Friendly Format
  const chartData = Object.entries(ordersByDay || {}).map(([date, stats]: any) => ({
    date, // Keep date as the key
    completed: stats.completed || 0,
    intransit: stats.inTransit || 0,
    expected: stats.expected || 0, // Assuming assigned = confirmed
    draft: stats.draft || 0,
    failed: stats.failed || 0,
    outfordelivery: stats.outForDelivery || 0,
    awaitingnextattempt: stats.awaitingNextAttempt || 0,
  }));

  // Function to open orders page for a specific day with all statuses
  const openOrdersPage = (date) => {
    const statuses = [
      'COMPLETED',
      'EXPECTED',
      'DRAFT',
      'AWAITING_NEXT_ATTEMPT',
      'OUT_FOR_DELIVERY',
      'RETURNED',
      'IN_TRANSIT',
    ].join(','); // Generate "completed,intransit,confirmed,draft,failed"
    history.push(`/orders?createdAtFrom=${date}&createdAtTo=${date}&statuses=${encodeURIComponent(statuses)}`);
  };

  return (
    <Card className="min-h-[400px]">
      <CardHeader>
        <CardTitle>Orders by Day</CardTitle>
        <CardDescription>{range === 'last-week' ? 'Last week' : 'Last 7 days'}</CardDescription>
      </CardHeader>
      <CardContent>
        <ChartContainer config={chartConfig}>
          <BarChart width={500} height={300} data={chartData} margin={{ top: 20, left: 20, right: 20, bottom: 20 }}>
            <CartesianGrid vertical={false} strokeDasharray="3 3" />
            <XAxis
              dataKey="date"
              axisLine={false}
              tickLine={false}
              tickMargin={10}
              tickFormatter={(date) => {
                const [, month, day] = date.split('-');
                return `${day}/${month}`;
              }}
            />
            <YAxis hide />
            <ChartTooltip content={<ChartTooltipContent hideLabel />} />
            <ChartLegend content={<ChartLegendContent />} />

            {Object.keys(chartConfig).map((status, index) => (
              <Bar
                key={status}
                dataKey={status}
                stackId="orders"
                fill={chartConfig[status].color}
                radius={[0, 0, 0, 0]}
              >
                {chartData.map((entry, i) => (
                  <Cell key={`cell-${index}-${i}`} cursor="pointer" onClick={() => openOrdersPage(entry.date)} />
                ))}
              </Bar>
            ))}
          </BarChart>
        </ChartContainer>
      </CardContent>
      <CardFooter className="flex-col items-start gap-2 text-sm">
        <div className="leading-none text-muted-foreground">Click a bar to view orders for that day</div>
      </CardFooter>
    </Card>
  );
}
